import {
  deserializeDateRange,
  serializeDateRange,
} from "@repo/app-types/entities/dateRange";
import type {
  OutcomeCondition,
  PlaybookCondition,
} from "@repo/app-types/entities/filterConditions";
import {
  getPlaybookViewFilterDefaults,
  type PlaybookId,
  type PlaybookView,
} from "@repo/app-types/entities/playbook";
import type {
  PlaybookViewDefaultsMap,
  PlaybookViewFilterDefaults,
} from "@repo/app-types/entities/playbookViewDefaults";
import { checkIsFilterEqual } from "./filterEquality.js";
import type { FilterDefaults } from "./filters.js";
import type { FilterKey, FilterValues } from "./filterState.js";

/*
 * A condition names the playbook it was built for. One built for another
 * playbook is inert wherever it lands — no chip renders it and no query sends
 * it — so it must not reach a playbook's defaults either, in or out. The
 * filter bar's state is app-wide and outlives the page it was set on, which is
 * how one gets carried somewhere it doesn't belong.
 */

function keepOwnOutcome(
  outcome: OutcomeCondition | null | undefined,
  playbookId: PlaybookId,
): OutcomeCondition | null {
  return outcome?.playbookId === playbookId ? outcome : null;
}

function keepOwnPlaybookConditions(
  conditions: readonly PlaybookCondition[],
  playbookId: PlaybookId,
): readonly PlaybookCondition[] {
  return conditions.filter((condition) => condition.playbookId === playbookId);
}

/**
 * What a playbook says a page of this kind should start on, in the shapes the
 * filter bar holds.
 *
 * Only what the playbook actually configured: an absent filter is left out
 * rather than filled in, so it falls through to whatever the app would have
 * defaulted to — for the date range, the company's setting and then the
 * built-in window, both of which the app-level `FilterProvider` applies.
 *
 * A stored range that no longer parses is dropped the same way, as is a
 * condition belonging to another playbook. Either would take precedence over
 * the fallbacks while being invisible on the page: no widget renders a filter
 * it can't resolve, and no query sends one.
 */
export function resolvePlaybookViewFilterDefaults(
  playbook:
    | { id: PlaybookId; view_defaults: PlaybookViewDefaultsMap }
    | undefined,
  view: PlaybookView,
): FilterDefaults {
  const stored = getPlaybookViewFilterDefaults(playbook, view);
  if (!playbook || !stored) {
    return {};
  }

  const dateRange = stored.date_range
    ? deserializeDateRange(stored.date_range)
    : undefined;
  const outcome = keepOwnOutcome(stored.outcome, playbook.id);

  return {
    ...(dateRange ? { dateRange } : {}),
    ...(stored.date_basis ? { dateBasis: stored.date_basis } : {}),
    ...(stored.playbook_conditions
      ? {
          playbookConditions: keepOwnPlaybookConditions(
            stored.playbook_conditions,
            playbook.id,
          ),
        }
      : {}),
    ...(stored.custom_fields ? { customFields: stored.custom_fields } : {}),
    ...(stored.outcome !== undefined ? { outcome } : {}),
    ...(stored.signal_id !== undefined ? { signalId: stored.signal_id } : {}),
    ...(stored.signal_metadata_fields
      ? { signalMetadataFields: stored.signal_metadata_fields }
      : {}),
    ...(stored.total_recording_duration !== undefined
      ? { totalRecordingDuration: stored.total_recording_duration }
      : {}),
    ...(stored.recording_count !== undefined
      ? { recordingCount: stored.recording_count }
      : {}),
    ...(stored.coaching_goal !== undefined
      ? { coachingGoal: stored.coaching_goal }
      : {}),
  };
}

/**
 * The filters a page can save as the playbook's default, in the order the
 * stored object lists them.
 *
 * Not every filter on the bar: the teammate-group ones answer "whose calls",
 * which each viewer decides for themselves — `FilterProvider` already saves
 * that choice to the teammate's own preferences, and a playbook-wide default
 * would fight it on every page load.
 */
const SAVABLE_FILTER_KEYS = [
  "dateBasis",
  "dateRange",
  "playbookConditions",
  "customFields",
  "outcome",
  "signalId",
  "signalMetadataFields",
  "totalRecordingDuration",
  "recordingCount",
  "coachingGoal",
] as const satisfies readonly FilterKey[];

type SavableFilterKey = (typeof SAVABLE_FILTER_KEYS)[number];

/**
 * Whether this page gets to save the filter as a default at all.
 *
 * Two of them only count alongside another filter on the same page:
 * - the date basis, because a page without the picker is showing one the
 *   viewer chose somewhere else;
 * - the signal metadata conditions, because they only mean anything for the
 *   signal they were picked under. The drilldown takes its signal from the URL
 *   rather than the filter bar, so saving there would store conditions the
 *   default can't name a signal for, and they'd narrow other pages silently.
 */
function checkIsFilterSavable(
  filter: SavableFilterKey,
  filters: readonly FilterKey[],
): boolean {
  switch (filter) {
    case "dateBasis":
      return filters.includes("dateBasis");
    case "signalMetadataFields":
      return filters.includes("signalId");
    default:
      return true;
  }
}

/** The filter values as this playbook sees them, foreign conditions dropped. */
function keepOwnConditions(
  values: FilterValues,
  playbookId: PlaybookId,
): FilterValues {
  return {
    ...values,
    outcome: keepOwnOutcome(values.outcome, playbookId),
    playbookConditions: keepOwnPlaybookConditions(
      values.playbookConditions,
      playbookId,
    ),
  };
}

function buildFilterDefault(
  filter: SavableFilterKey,
  values: FilterValues,
): PlaybookViewFilterDefaults {
  switch (filter) {
    case "dateBasis":
      return { date_basis: values.dateBasis };
    case "dateRange":
      return { date_range: serializeDateRange(values.dateRange) };
    case "playbookConditions":
      return { playbook_conditions: [...values.playbookConditions] };
    case "customFields":
      return { custom_fields: [...values.customFields] };
    case "outcome":
      return { outcome: values.outcome };
    case "signalId":
      return { signal_id: values.signalId };
    case "signalMetadataFields":
      return { signal_metadata_fields: [...values.signalMetadataFields] };
    case "totalRecordingDuration":
      return { total_recording_duration: values.totalRecordingDuration };
    case "recordingCount":
      return { recording_count: values.recordingCount };
    case "coachingGoal":
      return { coaching_goal: values.coachingGoal };
  }
}

/**
 * The filters to save as this playbook's default for this kind of page, or
 * `undefined` when the viewer is looking at the defaults already.
 *
 * A patch of what they changed rather than everything they can see: the
 * filters they left alone keep falling through to the company's settings, so
 * saving a narrower outcome doesn't quietly freeze this playbook's date range
 * as a side effect. What the next teammate lands on is still what the viewer
 * was looking at when they saved.
 */
export function buildPlaybookViewFilterDefaultsPatch(
  playbookId: PlaybookId,
  filters: readonly FilterKey[],
  values: FilterValues,
  defaultValues: FilterValues,
): PlaybookViewFilterDefaults | undefined {
  const ownValues = keepOwnConditions(values, playbookId);
  const ownDefaults = keepOwnConditions(defaultValues, playbookId);

  const changed = SAVABLE_FILTER_KEYS.filter(
    (filter) =>
      filters.includes(filter) &&
      checkIsFilterSavable(filter, filters) &&
      !checkIsFilterEqual(filter, ownValues[filter], ownDefaults[filter]),
  );

  if (changed.length === 0) {
    return undefined;
  }

  return changed.reduce<PlaybookViewFilterDefaults>(
    (patch, filter) => ({ ...patch, ...buildFilterDefault(filter, ownValues) }),
    {},
  );
}
